import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Cpu,
  Files,
  LayoutDashboard,
  MessageSquare,
  Plus,
  Settings,
  Users,
  Workflow,
} from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
  SectionNavLink,
  sectionNavLinkClassName,
} from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import {
  nextgenConversationPath,
  nextgenProjectChatsPath,
  nextgenProjectPath,
  nextgenProjectSessionsPath,
  nextgenProjectSettingsPath,
} from "~/pages/manifold-nextgen/paths";
import { ProjectConversation } from "~/services/projects";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";
import { useSessionActions } from "./useSessionActions";

export interface ProjectSidebarProps {
  projectId: string;
  sessions: ProjectConversation[] | undefined;
}

// How many sessions the sidebar shows before collapsing behind "View all".
const SESSIONS_SIDEBAR_LIMIT = 5;

interface SessionRowProps {
  projectId: string;
  session: ProjectConversation;
  isRenaming: boolean;
  onStartRename: () => void;
  onCommitRename: (next: string) => void;
  onEndRename: () => void;
  onShare: () => void;
  onDelete: () => void;
}

const SessionRow = ({
  projectId,
  session,
  isRenaming,
  onStartRename,
  onCommitRename,
  onEndRename,
  onShare,
  onDelete,
}: SessionRowProps) => {
  const [draft, setDraft] = useState(session.display_name);
  const inputRef = useRef<HTMLInputElement>(null);

  const [flashing, setFlashing] = useState(false);
  const prevNameRef = useRef(session.display_name);

  useEffect(() => {
    if (prevNameRef.current === session.display_name) {
      return;
    }
    prevNameRef.current = session.display_name;
    setFlashing(true);
    const id = window.setTimeout(() => setFlashing(false), 900);
    return () => window.clearTimeout(id);
  }, [session.display_name]);

  useEffect(() => {
    if (isRenaming) {
      setDraft(session.display_name);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isRenaming, session.display_name]);

  const commit = () => {
    onCommitRename(draft);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  return (
    <li className="group relative cursor-pointer">
      <NavLink
        to={nextgenConversationPath(projectId, session.id)}
        className={({ isActive }) =>
          cn(
            "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
            isActive
              ? "bg-black/[0.06] text-foreground dark:bg-white/[0.08]"
              : "text-muted-foreground hover:bg-black/[0.06] hover:text-foreground dark:hover:bg-white/[0.08]"
          )
        }
        onClick={(e) => {
          if (isRenaming) {
            e.preventDefault();
          }
        }}
      >
        {session.agent ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">
                <AgentAvatar agent={session.agent} size={18} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{session.agent.display_name}</TooltipContent>
          </Tooltip>
        ) : (
          <div
            className="flex size-[18px] shrink-0 items-center justify-center text-current"
            aria-hidden="true"
          >
            <MessageSquare className="size-3" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.preventDefault()}
              aria-label="Rename session"
              className="w-full rounded border border-border bg-background px-1 py-0 text-xs font-medium text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span
              className={cn(
                "block truncate pr-4 text-sm font-medium transition-colors duration-700",
                flashing && "text-primary"
              )}
            >
              {session.display_name}
            </span>
          )}
        </div>
      </NavLink>

      {!isRenaming && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <ConversationActionsMenu onShare={onShare} onRename={onStartRename} onDelete={onDelete} />
        </div>
      )}
    </li>
  );
};

const RecentSessionsList = ({
  projectId,
  sessions,
}: {
  projectId: string;
  sessions: ProjectConversation[];
}) => {
  const {
    renamingId,
    startRename,
    commitRename,
    endRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  } = useSessionActions(projectId);

  return (
    <>
      <ul className="flex flex-col gap-0.5" role="list">
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            projectId={projectId}
            session={session}
            isRenaming={renamingId === session.id}
            onStartRename={() => startRename(session.id)}
            onCommitRename={(next) => commitRename(session, next)}
            onEndRename={endRename}
            onShare={() => setActionTarget({ session, action: "share" })}
            onDelete={() => setActionTarget({ session, action: "delete" })}
          />
        ))}
      </ul>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationId={actionTarget?.session.id ?? ""}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationTitle={actionTarget?.session.display_name ?? ""}
        onConfirm={confirmDelete}
      />
    </>
  );
};

const ProjectNavLink = (props: {
  to: string;
  icon: ReactNode;
  label: string;
  end?: boolean;
  badge?: number;
}) => (
  <li>
    <SectionNavLink {...props} className="px-2 py-1.5" />
  </li>
);

// Placeholder for features that don't exist yet but belong in the project IA.
const ProjectNavPlaceholder = ({ icon, label }: { icon: ReactNode; label: string }) => (
  <li>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex cursor-default items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground/40">
          <span className="shrink-0">{icon}</span>
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right">Coming soon</TooltipContent>
    </Tooltip>
  </li>
);

export const ProjectSidebar = ({ projectId, sessions }: ProjectSidebarProps) => {
  return (
    <SectionSidebar ariaLabel="Project navigation">
      <ul className="flex flex-col gap-0.5 px-4 pt-4">
        <ProjectNavLink
          to={nextgenProjectPath(projectId)}
          end
          icon={<LayoutDashboard className="size-4" />}
          label="Overview"
        />
        <ProjectNavPlaceholder icon={<Files className="size-4" />} label="Files" />
        <ProjectNavPlaceholder icon={<Cpu className="size-4" />} label="Compute Envs" />
        <ProjectNavPlaceholder icon={<Workflow className="size-4" />} label="Workflows" />
        <ProjectNavPlaceholder icon={<Users className="size-4" />} label="Cohorts" />
        <ProjectNavLink
          to={nextgenProjectSettingsPath(projectId)}
          end
          icon={<Settings className="size-4" />}
          label="Settings"
        />
      </ul>

      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-2 pt-3">
        <p className="py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Sessions
        </p>
        <NavLink
          to={nextgenProjectChatsPath(projectId)}
          end
          className={(state) => sectionNavLinkClassName(state, "mb-1 w-full px-2 py-1.5")}
        >
          <Plus className="size-4 shrink-0" />
          <span className="flex-1 text-left">New Session</span>
        </NavLink>
        {sessions === undefined ? (
          <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-center gap-2 py-1.5">
                <Skeleton className="size-[18px] shrink-0 rounded-full" />
                <Skeleton className="h-3 w-28" />
              </li>
            ))}
          </ul>
        ) : sessions.length === 0 ? (
          <p className="py-1 text-xs italic text-muted-foreground/80">No sessions yet.</p>
        ) : (
          <>
            <RecentSessionsList
              projectId={projectId}
              sessions={sessions.slice(0, SESSIONS_SIDEBAR_LIMIT)}
            />
            {sessions.length > SESSIONS_SIDEBAR_LIMIT && (
              <Link
                to={nextgenProjectSessionsPath(projectId)}
                className="mt-1 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                View all
              </Link>
            )}
          </>
        )}
      </div>
    </SectionSidebar>
  );
};
